import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchShipmentOptions, orderTrackingAction } from '../../api/orders';
import {
  inputClassName,
  labelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
} from '../../components/ui';

const FALLBACK_IN_CARRIERS = [{ code: 'Other', label: 'Other' }];

function isEkartName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[\s._-]+/g, '')
    .includes('ekart');
}

function amazonSelectCarrier(code) {
  const next = String(code || '').trim();
  if (!next || isEkartName(next)) return 'Other';
  return next;
}

function todayInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toShipDateIso(dateValue) {
  if (!dateValue) return '';

  const today = todayInputValue();
  if (dateValue >= today) {
    return new Date(Date.now() - 5000).toISOString();
  }

  const noonLocal = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(noonLocal.getTime())) {
    return '';
  }

  if (noonLocal.getTime() > Date.now()) {
    return new Date(Date.now() - 5000).toISOString();
  }

  return noonLocal.toISOString();
}

function clampShipDate(value, minDate, maxDate) {
  let next = value || todayInputValue();
  if (minDate && next < minDate) next = minDate;
  if (maxDate && next > maxDate) next = maxDate;
  return next;
}

/**
 * Bulk Self Ship confirm for selected procurement lines.
 * Amazon has no true multi-order confirm API here — we call confirmShipment per order
 * with shared carrier / ship date, each line keeping its own dummy tracking.
 */
export default function BulkConfirmShipmentModal({
  isOpen,
  onClose,
  onComplete,
  token,
  targets = [],
  marketplace = 'IN',
  hideShipDate = false,
  title = 'Confirm selected shipments',
  submitLabel,
  continueOnClose = false,
  onProgress,
  perOrderCarrier,
}) {
  const usePerOrderCarrier = Boolean(perOrderCarrier ?? hideShipDate);
  const [carriers, setCarriers] = useState([]);
  const [shippingServices, setShippingServices] = useState([{ code: 'Other', label: 'Other' }]);
  const [shipDate, setShipDate] = useState(todayInputValue());
  const [minShipDate, setMinShipDate] = useState('');
  const [maxShipDate, setMaxShipDate] = useState(todayInputValue());
  const [carrierCode, setCarrierCode] = useState('Other');
  const [carrierName, setCarrierName] = useState('');
  const [shippingServiceCode, setShippingServiceCode] = useState('Other');
  const [shippingMethod, setShippingMethod] = useState('Standard');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [carrierByOrderId, setCarrierByOrderId] = useState({});
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!isOpen || submittingRef.current) {
      return undefined;
    }

    let cancelled = false;
    const today = todayInputValue();
    setShipDate(today);
    setMinShipDate('');
    setMaxShipDate(today);
    setCarrierName('');
    setShippingServiceCode('Other');
    setShippingMethod('Standard');
    setError('');
    setResults(null);
    setProgress({ done: 0, total: targets.length });
    setCarrierByOrderId(
      Object.fromEntries(
        (targets || []).map((target) => [
          target.orderId,
          amazonSelectCarrier(target.carrierCode || target.initialCarrierCode),
        ])
      )
    );
    setLoadingOptions(true);

    (async () => {
      try {
        const payload = await fetchShipmentOptions(token, marketplace || 'IN', {});
        if (cancelled) return;
        const apiNames = (targets || []).flatMap((target) => [
          target.bookedCourier,
          target.carrierCode,
          target.initialCarrierCode,
        ]);
        const nextCarriers = (() => {
          const list = [...(payload.carriers || FALLBACK_IN_CARRIERS)].filter(
            (entry) => !isEkartName(entry.code)
          );
          for (const name of apiNames) {
            const code = String(name || '').trim();
            if (!code || isEkartName(code)) continue;
            if (list.some((entry) => String(entry.code || '').toLowerCase() === code.toLowerCase())) {
              continue;
            }
            const otherIdx = list.findIndex((entry) => entry.code === 'Other');
            const entry = { code, label: code };
            if (otherIdx >= 0) list.splice(otherIdx, 0, entry);
            else list.push(entry);
          }
          if (!list.some((entry) => entry.code === 'Other')) {
            list.push({ code: 'Other', label: 'Other' });
          }
          return list;
        })();
        setCarriers(nextCarriers);
        setShippingServices(
          payload.shippingServices?.length
            ? payload.shippingServices
            : [{ code: 'Other', label: 'Other' }]
        );
        setCarrierCode('Other');
        setMaxShipDate(payload.maxShipDate || today);
        setMinShipDate(payload.minShipDate || payload.orderDate || '');
        setShipDate(
          clampShipDate(
            payload.suggestedShipDate || today,
            payload.minShipDate || '',
            payload.maxShipDate || today
          )
        );
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load Amazon carrier list.');
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, token, marketplace, targets.length]);

  if (!isOpen) {
    return null;
  }

  const handleHide = () => {
    if (submitting && !continueOnClose) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setResults(null);

    if (!targets.length) {
      setError('No orders selected.');
      return;
    }

    const today = todayInputValue();
    if (!hideShipDate) {
      if (!shipDate || shipDate > today || (maxShipDate && shipDate > maxShipDate)) {
        setError(
          `Ship date cannot be in the future. Amazon only accepts dates up to today (${maxShipDate || today}).`
        );
        return;
      }

      if (minShipDate && shipDate < minShipDate) {
        setError(`Ship date cannot be before ${minShipDate}.`);
        return;
      }
    }

    if (!usePerOrderCarrier) {
      if (!carrierCode) {
        setError('Carrier is required.');
        return;
      }

      if (carrierCode === 'Other' && !carrierName.trim()) {
        setError('Enter a carrier name when Carrier is Other.');
        return;
      }
    }

    if (shippingServiceCode === 'Other' && !shippingMethod.trim()) {
      setError('Enter a shipping service name.');
      return;
    }

    const shipDateIso = hideShipDate ? '' : toShipDateIso(shipDate);
    if (!hideShipDate && !shipDateIso) {
      setError('Invalid ship date.');
      return;
    }

    const shippingMethodValue =
      shippingServiceCode === 'Other'
        ? shippingMethod.trim() || 'Other'
        : shippingServiceCode;

    const jobTargets = [...targets];
    submittingRef.current = true;
    setSubmitting(true);
    setProgress({ done: 0, total: jobTargets.length });

    const succeeded = [];
    const failed = [];

    for (let i = 0; i < jobTargets.length; i += 1) {
      const target = jobTargets[i];
      try {
        const rowCarrier = amazonSelectCarrier(
          usePerOrderCarrier
            ? carrierByOrderId[target.orderId] || target.carrierCode || carrierCode
            : carrierCode
        );
        const payload = await orderTrackingAction(token, target.orderId, 'push', {
          ...(hideShipDate
            ? { preserveShipDate: true, hideShipDate: true }
            : { shipDate: shipDateIso }),
          carrierCode: rowCarrier,
          carrierName:
            rowCarrier === 'Other'
              ? (
                  usePerOrderCarrier
                    ? target.carrierName || target.bookedCourier || carrierName
                    : carrierName
                ).trim()
              : undefined,
          shippingMethod: shippingMethodValue,
          trackingNumber: target.trackingNumber,
        });
        succeeded.push({
          lineId: target.lineId,
          orderId: target.orderId,
          amazonOrderId: target.amazonOrderId,
          payload,
        });
      } catch (err) {
        failed.push({
          lineId: target.lineId,
          orderId: target.orderId,
          amazonOrderId: target.amazonOrderId,
          message: err.message || 'Confirm failed.',
        });
      }
      const nextProgress = { done: i + 1, total: jobTargets.length };
      setProgress(nextProgress);
      onProgress?.(nextProgress);
    }

    submittingRef.current = false;
    setSubmitting(false);
    const summary = { succeeded, failed };
    setResults(summary);
    onComplete?.(summary);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={submitting && !continueOnClose ? undefined : handleHide}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-confirm-shipment-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="bulk-confirm-shipment-title" className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {targets.length} order{targets.length === 1 ? '' : 's'}
              {usePerOrderCarrier
                ? ' — booked ITL courier is shown per order. Edit Amazon courier if it is wrong. Original ship date is kept.'
                : hideShipDate
                  ? ' — shared Amazon courier; each packet keeps its I2I waybill. Original ship date is kept.'
                  : ' — shared carrier & ship date; each keeps its own dummy tracking.'}
              {continueOnClose ? ' Close hides this box — Amazon push keeps running.' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={handleHide}
            disabled={submitting && !continueOnClose}
            className="rounded-md px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            Close
          </button>
        </div>

        {usePerOrderCarrier ? (
          <div className="mb-4 overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[36rem] border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Order ID</th>
                  <th className="px-3 py-2">Waybill</th>
                  <th className="px-3 py-2">Booked courier</th>
                  <th className="px-3 py-2">Push to Amazon as</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => {
                  const carrierOptions = carriers.length ? carriers : FALLBACK_IN_CARRIERS;
                  const selected = amazonSelectCarrier(
                    carrierByOrderId[target.orderId] || target.carrierCode || 'Other'
                  );
                  const known = carrierOptions.some((entry) => entry.code === selected);
                  return (
                    <tr key={target.lineId || target.orderId} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-800">
                        {target.amazonOrderId}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        {target.trackingNumber}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-slate-800">
                        {target.bookedCourier || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className={`${inputClassName} h-8 min-w-[9.5rem] py-0 text-xs`}
                          value={selected}
                          disabled={submitting || loadingOptions}
                          onChange={(event) =>
                            setCarrierByOrderId((current) => ({
                              ...current,
                              [target.orderId]: event.target.value,
                            }))
                          }
                        >
                          {known ? null : <option value={selected}>{selected}</option>}
                          {carrierOptions.map((entry) => (
                            <option key={entry.code} value={entry.code}>
                              {entry.label || entry.code}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
        <ul className="mb-4 max-h-56 space-y-1.5 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          {targets.map((target) => (
            <li key={target.lineId || target.orderId} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate font-mono">{target.amazonOrderId}</span>
              <span className="shrink-0 font-mono text-slate-500">{target.trackingNumber}</span>
            </li>
          ))}
        </ul>
        )}

        {results ? (
          <div className="grid gap-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              Succeeded: {results.succeeded.length}
            </div>
            {results.failed.length ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                <p className="font-semibold">Failed: {results.failed.length}</p>
                <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto text-xs">
                  {results.failed.map((item) => (
                    <li key={item.lineId || item.orderId}>
                      <span className="font-mono">{item.amazonOrderId}</span> — {item.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="flex justify-end">
              <button type="button" className={primaryButtonClassName} onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="grid gap-4" onSubmit={handleSubmit}>
            {hideShipDate ? null : (
            <label className="grid gap-1.5">
              <span className={labelClassName}>Ship date</span>
              <input
                type="date"
                value={shipDate}
                min={minShipDate || undefined}
                max={maxShipDate || todayInputValue()}
                onChange={(event) =>
                  setShipDate(
                    clampShipDate(event.target.value, minShipDate, maxShipDate || todayInputValue())
                  )
                }
                className={inputClassName}
                required
                disabled={submitting}
              />
            </label>
            )}

            {usePerOrderCarrier ? null : (
            <>
            <label className="grid gap-1.5">
              <span className={labelClassName}>Carrier</span>
              <select
                value={carrierCode}
                onChange={(event) => setCarrierCode(event.target.value)}
                className={inputClassName}
                disabled={loadingOptions || !carriers.length || submitting}
                required
              >
                {carriers.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label || entry.code}
                  </option>
                ))}
              </select>
            </label>

            {carrierCode === 'Other' ? (
              <label className="grid gap-1.5">
                <span className={labelClassName}>Carrier name</span>
                <input
                  type="text"
                  value={carrierName}
                  onChange={(event) => setCarrierName(event.target.value)}
                  className={inputClassName}
                  required
                  disabled={submitting}
                />
              </label>
            ) : null}
            </>
            )}

            <label className="grid gap-1.5">
              <span className={labelClassName}>Shipping service</span>
              <select
                value={shippingServiceCode}
                onChange={(event) => setShippingServiceCode(event.target.value)}
                className={inputClassName}
                disabled={submitting}
              >
                {shippingServices.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label || entry.code}
                  </option>
                ))}
              </select>
            </label>

            {shippingServiceCode === 'Other' ? (
              <label className="grid gap-1.5">
                <span className={labelClassName}>Shipping service name</span>
                <input
                  type="text"
                  value={shippingMethod}
                  onChange={(event) => setShippingMethod(event.target.value)}
                  className={inputClassName}
                  required
                  disabled={submitting}
                />
              </label>
            ) : null}

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </div>
            ) : null}

            {submitting ? (
              <p className="text-sm text-slate-600">
                Confirming {progress.done} / {progress.total} on Amazon…
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={handleHide}
                disabled={submitting && !continueOnClose}
              >
                {submitting && continueOnClose ? 'Hide' : 'Cancel'}
              </button>
              <button type="submit" className={primaryButtonClassName} disabled={submitting || loadingOptions}>
                {submitting
                  ? `Pushing… ${progress.done}/${progress.total}`
                  : submitLabel || `Confirm ${targets.length} on Amazon`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
